import i18n from '@fiverr/i18n/singleton';
import type { DisplayableCardInfo, EagerJobInfo, GetJobInfoArgs, ShouldNotDisplayCard } from '@fiverr-private/jtbd_sdk';
import { ICON_URL } from '../constants';
import { BUSINESS_PURPOSE, COMPANY_SIZE } from '../../types';
import { fetchBusinessInfo } from './fetchBusinessInfo';

export const SELLER_COUNTRIES = ['IN', 'PK', 'BD', 'PH', 'TH', 'ID', 'SD', 'NG', 'LK', 'KE', 'MA'];

export const isFromSellerCountry = (country: string): boolean => SELLER_COUNTRIES.includes(country);

const doNotShowTemplate: ShouldNotDisplayCard = {
    shouldDisplayJob: false,
};

const getDisplayCardConfig = ({ locale, userId }): EagerJobInfo => ({
    shouldDisplayJob: true,
    dismissInfo: {
        isDismissible: false,
    },
    headerText: i18n.t('jobs_tbd.sourcing.header_text', { $scope: locale }),
    jobPages: [
        {
            title: i18n.t('jobs_tbd.sourcing.title', { $scope: locale }),
            subtitle: i18n.t('jobs_tbd.sourcing.subtitle', { $scope: locale }),
            iconUrl: ICON_URL,
            ctaText: i18n.t('jobs_tbd.sourcing.cta', { $scope: locale }),
            biEnrichment: {
                user: {
                    id: userId,
                },
                page: {
                    element: {
                        name: 'JTBD_sourcing',
                    },
                },
            },
        },
    ],
    clickAction: {
        type: 'custom',
    },
});

const shouldDisplayCard = ({
    companySize,
    purposes,
    avgOrderAmount,
    countryCode,
}: {
    companySize: COMPANY_SIZE | string | undefined | null;
    purposes: BUSINESS_PURPOSE[] | string[] | null | undefined;
    avgOrderAmount: number | null | undefined;
    countryCode: string;
}): boolean => {
    if (isFromSellerCountry(countryCode)) {
        return false;
    }

    if (companySize && companySize !== COMPANY_SIZE.ONLY_ME) {
        return true;
    }

    if (purposes?.length && purposes.includes('business')) {
        return true;
    }

    if (avgOrderAmount && avgOrderAmount >= 10000) {
        return true;
    }

    return false;
};

export const getJobInfo = async ({
    context,
    externalServicesInfo,
    buyerInsights,
}: GetJobInfoArgs): Promise<ShouldNotDisplayCard | DisplayableCardInfo> => {
    const { locale, userId, countryCode } = context;

    if (!buyerInsights.segment.buyerType) {
        return doNotShowTemplate;
    }

    const displayCardConfig = getDisplayCardConfig({ locale, userId });

    const federationPegasusUrl = externalServicesInfo?.federationPegasus?.url;

    const businessInfo = await fetchBusinessInfo(federationPegasusUrl, userId as number);

    const showCard = shouldDisplayCard({
        companySize: businessInfo.companySize,
        purposes: businessInfo.purposes,
        avgOrderAmount: businessInfo.avgOrderAmount,
        countryCode,
    });

    return showCard ? displayCardConfig : doNotShowTemplate;
};
